import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { renderAsync } from 'docx-preview';
import { apiService } from '../services/api';
import Layout from '../components/Layout/Layout';
import TemplatesSidebar from '../components/TemplatesSidebar';
import { API_BASE_URL } from '../config';

const ViewTemplate: React.FC = () => {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const [template, setTemplate] = useState<any>(null);
  const [fileArrayBuffer, setFileArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [savingVariables, setSavingVariables] = useState(false);
  const [variables, setVariables] = useState<any[]>([]);
  const [originalVariables, setOriginalVariables] = useState<any[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Check user scopes
  const hasTemplatesEditor = () => {
    return user?.scopes?.includes('templates.editor') || false;
  };

  const hasTemplatesView = () => {
    return user?.scopes?.includes('templates.view') || false;
  };

  // Load groups saat component mount
  useEffect(() => {
    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const groupsData = await apiService.getGroups();
        setGroups(groupsData);
      } catch (err: any) {
        console.error('Error loading groups:', err);
      } finally {
        setLoadingGroups(false);
      }
    };
    loadGroups();
  }, []);

  // Load template data dari API
  useEffect(() => {
    if (!id) {
      navigate('/templates');
      return;
    }
    loadTemplate();
  }, [id, navigate]);

  // Render dokumen saat file sudah loaded
  useEffect(() => {
    if (!fileArrayBuffer || isLoading) {
      return;
    }

    const renderDocument = async () => {
      let retryCount = 0;
      const maxRetries = 20;

      const checkAndRender = async () => {
        if (!previewContainerRef.current) {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndRender, 100);
            return;
          } else {
            console.error('Container tidak ditemukan setelah beberapa retry');
            setError('Gagal menampilkan preview dokumen');
            return;
          }
        }

        try {
          // Clear previous content
          previewContainerRef.current.innerHTML = '';

          await renderAsync(
            fileArrayBuffer,
            previewContainerRef.current!,
            undefined,
            {
              className: 'docx-wrapper',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: false
            }
          );
        } catch (err) {
          console.error('Error rendering document:', err);
          setError('Gagal menampilkan preview dokumen');
        }
      };

      setTimeout(checkAndRender, 200);
    };

    renderDocument();
  }, [fileArrayBuffer, isLoading]);

  const loadTemplate = async () => {
    if (!id) return;

    setIsLoading(true);
    setError('');

    try {
      // Load template data dari API
      const templateData = await apiService.getTemplate(Number(id));
      setTemplate(templateData);

      // Set variables dari template
      const vars = templateData.variables && Array.isArray(templateData.variables)
        ? templateData.variables
        : [];
      setVariables(vars);
      setOriginalVariables(JSON.parse(JSON.stringify(vars))); // Deep copy untuk tracking perubahan
      setHasUnsavedChanges(false);

      // Load file dari backend menggunakan endpoint API
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE_URL}/api/templates/${id}/file`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Gagal memuat file template');
      }

      const arrayBuffer = await response.arrayBuffer();
      setFileArrayBuffer(arrayBuffer);
    } catch (err: any) {
      console.error('Error loading template:', err);
      setError(err.message || 'Gagal memuat template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVariableGroupChange = (variableIndex: number, groupId: number | null) => {
    // Update variable di local state saja (tidak langsung save)
    const updatedVariables = [...variables];
    updatedVariables[variableIndex] = {
      ...updatedVariables[variableIndex],
      group_id: groupId
    };
    setVariables(updatedVariables);

    // Check apakah ada perubahan dengan membandingkan dengan originalVariables
    const hasChanges = JSON.stringify(updatedVariables) !== JSON.stringify(originalVariables);
    setHasUnsavedChanges(hasChanges);
  };

  const handleSaveVariables = async () => {
    if (!id || !template || !hasUnsavedChanges) return;

    setSavingVariables(true);
    setError('');
    try {
      // Update template di backend
      await apiService.updateTemplate(Number(id), {
        variables: variables
      });

      // Update template state juga
      setTemplate({
        ...template,
        variables: variables
      });

      // Update originalVariables untuk reset tracking
      setOriginalVariables(JSON.parse(JSON.stringify(variables)));
      setHasUnsavedChanges(false);
    } catch (err: any) {
      console.error('Error saving variables:', err);
      setError('Gagal menyimpan perubahan. Silakan coba lagi.');
    } finally {
      setSavingVariables(false);
    }
  };

  const handleCancelChanges = async () => {
    if (!id) return;

    try {
      // Reload template data dari backend untuk revert changes
      const templateData = await apiService.getTemplate(Number(id));
      setTemplate(templateData);
      const vars = templateData.variables && Array.isArray(templateData.variables)
        ? templateData.variables
        : [];
      setVariables(vars);
      setOriginalVariables(JSON.parse(JSON.stringify(vars)));
      setHasUnsavedChanges(false);
    } catch (err: any) {
      console.error('Error reverting changes:', err);
      setError('Gagal membatalkan perubahan. Silakan refresh halaman.');
    }
  };

  const getGroupName = (groupId: number | null | undefined): string => {
    if (!groupId) return 'Belum ditentukan';
    const group = groups.find((g: any) => g.id === groupId);
    return group ? group.name : 'Group tidak ditemukan';
  };

  const handleDownload = async () => {
    if (!template || !id) return;

    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE_URL}/api/templates/${id}/file`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Gagal mengunduh file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.file_name || 'template.docx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error downloading file:', err);
      setError(err.message || 'Gagal mengunduh file');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setDeleting(true);
    setError('');
    try {
      await apiService.deleteTemplate(Number(id));
      // Navigate back to templates list after successful deletion
      navigate('/templates');
    } catch (err: any) {
      console.error('Error deleting template:', err);
      setError(err.message || 'Gagal menghapus template');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  // Cleanup saat component unmount
  useEffect(() => {
    return () => {
      if (previewContainerRef.current) {
        previewContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  if (isLoading && !template) {
    return (
      <Layout
        sidebarContent={<TemplatesSidebar isCollapsed={false} />}
        sidebarCollapsedContent={<TemplatesSidebar isCollapsed={true} />}
      >
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Memuat template...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !template) {
    return (
      <Layout
        sidebarContent={<TemplatesSidebar isCollapsed={false} />}
        sidebarCollapsedContent={<TemplatesSidebar isCollapsed={true} />}
      >
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <i className="fa-solid fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/templates')}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              Kembali ke Templates
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <Layout
      sidebarContent={<TemplatesSidebar isCollapsed={false} />}
      sidebarCollapsedContent={<TemplatesSidebar isCollapsed={true} />}
    >
      <>
        <section
          className={`border-b px-6 py-4 fixed z-30 transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          style={{
            left: isCollapsed ? '64px' : '256px',
            right: 0,
            top: '56px'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <button
                onClick={() => navigate('/templates')}
                className={darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
              >
                <i className="fa-solid fa-arrow-left text-lg"></i>
              </button>
              <div className="flex-1">
                <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{template.title}</h1>
                <div className={`flex items-center space-x-4 mt-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {template.description && (
                    <span className="flex items-center">
                      <i className="fa-solid fa-file-lines mr-2"></i>
                      {template.description}
                    </span>
                  )}
                  {template.category && (
                    <span className="flex items-center">
                      <i className="fa-solid fa-folder mr-2"></i>
                      {template.category.name}
                    </span>
                  )}
                  {template.agreement_type && (
                    <span className="flex items-center">
                      <i className="fa-solid fa-file-signature mr-2"></i>
                      {template.agreement_type.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {hasTemplatesEditor() && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleting}
                  className={`px-4 py-2 border text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode
                    ? 'border-red-600 text-red-400 hover:bg-red-900/30'
                    : 'border-red-300 text-red-700 hover:bg-red-50'
                    }`}
                >
                  <i className="fa-solid fa-trash mr-2"></i>
                  Delete
                </button>
              )}
              <button
                onClick={handleDownload}
                disabled={downloading || !fileArrayBuffer}
                className={`px-4 py-2 border text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <i className={`fa-solid ${downloading ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`}></i>
                {downloading ? 'Downloading...' : 'Download Document'}
              </button>
              <button
                onClick={() => navigate('/new-request', {
                  state: {
                    templateId: template.id,
                    mode: 'use',
                    categoryId: template.category_id,
                    agreementTypeId: template.agreement_type_id,
                    title: template.title,
                    description: template.description,
                    category: template.category,
                    agreementType: template.agreement_type,
                    template: template,
                    filePath: template.file_path,
                    fileName: template.file_name,
                    variables: variables // Use updated variables state
                  }
                })}
                className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
              >
                <i className="fa-solid fa-check mr-2"></i>
                Use this template
              </button>
            </div>
          </div>
        </section>

        {error && (
          <div className={`px-6 py-3 border-l-4 transition-colors duration-300 ${darkMode ? 'bg-red-900/30 border-red-500 text-red-300' : 'bg-red-50 border-red-500 text-red-700'
            }`} style={{ marginTop: '100px' }}>
            <div className="flex items-center">
              <i className="fa-solid fa-exclamation-circle mr-2"></i>
              <span>{error}</span>
            </div>
          </div>
        )}

        <div style={{
          marginTop: error ? '120px' : '100px',
          height: error ? 'calc(100svh - 56px - 64px - 120px)' : 'calc(100svh - 56px - 64px - 100px)',
          overflow: 'hidden',
          maxHeight: error ? 'calc(100svh - 56px - 64px - 120px)' : 'calc(100svh - 56px - 64px - 100px)'
        }}>
          <div className="flex h-full">
            {/* Preview Section */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className={`px-6 py-4 border-b transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Preview Dokumen</h2>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>File: {template.file_name}</p>
              </div>
              <div
                ref={previewContainerRef}
                className={`docx-preview-container flex-1 overflow-y-auto overflow-x-hidden p-8 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'
                  }`}
                style={{
                  minHeight: 0
                }}
              >
                {isLoading && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat preview dokumen...</p>
                    </div>
                  </div>
                )}
                {!isLoading && !fileArrayBuffer && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <i className={`fa-solid fa-file-word text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Browser tidak mendukung preview dokumen Word</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Variables Section */}
            <div className={`w-80 border-l overflow-y-auto flex flex-col transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
              <div className={`px-6 py-4 border-b sticky top-0 z-10 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                <div className="flex items-center justify-between mb-2">
                  <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Variables</h2>
                  {hasUnsavedChanges && (
                    <span className={`text-xs font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-600'
                      }`}>
                      <i className="fa-solid fa-circle-exclamation mr-1"></i>
                      Ada perubahan
                    </span>
                  )}
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{variables.length} variables ditemukan</p>
                {hasUnsavedChanges && hasTemplatesEditor() && (
                  <div className="mt-3 flex items-center space-x-2">
                    <button
                      onClick={handleSaveVariables}
                      disabled={savingVariables}
                      className="flex-1 px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingVariables ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-floppy-disk mr-1"></i>
                          Simpan
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancelChanges}
                      disabled={savingVariables}
                      className={`px-3 py-1.5 border text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <i className="fa-solid fa-xmark mr-1"></i>
                      Batal
                    </button>
                  </div>
                )}
              </div>
              <div className="p-6 flex-1 overflow-y-auto">
                {variables.length === 0 ? (
                  <div className="text-center py-8">
                    <i className={`fa-solid fa-info-circle text-2xl mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tidak ada variables ditemukan</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {variables
                      .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                      .map((variable: any, index: number) => {
                        const sortedIndex = variables
                          .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                          .findIndex((v: any) => v.name === variable.name);
                        return (
                          <div
                            key={`${variable.name}-${variable.position}`}
                            className={`p-3 border rounded-lg transition-colors duration-300 ${darkMode
                              ? 'bg-violet-900/30 border-violet-700'
                              : 'bg-violet-50 border-violet-200'
                              }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium break-all ${darkMode ? 'text-violet-300' : 'text-violet-900'
                                  }`}>
                                  {variable.name}
                                </div>
                              </div>
                              <span className={`text-xs ml-2 flex-shrink-0 ${darkMode ? 'text-violet-400' : 'text-violet-600'
                                }`}>
                                #{sortedIndex + 1}
                              </span>
                            </div>
                            <div className="mt-2">
                              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                PIC Pengisi:
                              </label>
                              {hasTemplatesEditor() ? (
                                <select
                                  value={variable.group_id || ''}
                                  onChange={(e) => {
                                    const groupId = e.target.value ? Number(e.target.value) : null;
                                    handleVariableGroupChange(sortedIndex, groupId);
                                  }}
                                  disabled={loadingGroups || savingVariables}
                                  className={`w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode
                                    ? 'bg-gray-900 border-gray-700 text-white'
                                    : 'bg-white border-gray-300'
                                    }`}
                                >
                                  <option value="">-- Pilih Group --</option>
                                  {groups
                                    .filter((g: any) => g.is_active !== false)
                                    .map((group: any) => (
                                      <option key={group.id} value={group.id}>
                                        {group.name}
                                      </option>
                                    ))}
                                </select>
                              ) : (
                                <div className={`w-full px-2 py-1.5 text-xs border rounded-md transition-colors ${darkMode
                                  ? 'bg-gray-700 border-gray-600 text-gray-300'
                                  : 'bg-gray-50 border-gray-300 text-gray-700'
                                  }`}>
                                  {variable.group_id ? getGroupName(variable.group_id) : 'Belum ditentukan'}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`max-w-md w-full mx-4 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <i className="fa-solid fa-exclamation-triangle text-red-600 dark:text-red-400 text-xl"></i>
                </div>
                <h3 className={`ml-4 text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                  Hapus Template
                </h3>
              </div>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Apakah Anda yakin ingin menghapus template <strong>{template?.title}</strong>?
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className={`px-4 py-2 border text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Batal
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleting ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      Menghapus...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-trash mr-2"></i>
                      Hapus Template
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ViewTemplate;

